import { FC, useCallback, useRef } from 'react'
import { Box } from '../Box'
import { ChildRefCalback, FormGeneratorProps } from './types'

export const FormGenerator: FC<FormGeneratorProps> = ({
  formData,
  handleChange,
  errors,
  touched,
  values,
  setFieldValue,
  rightEnd,
  setFieldTouched,
  isSubmitting,
  onFormSubmit,
  isValid,
  changeStep,
  reloadStep,
  verticalSpacing = 8,
  horizontalFlex,
  verticalFlex,
  contentOverflow,
  contentPadding,
  allTouched,
}: FormGeneratorProps) => {
  const inputRef = Array(20)
    .fill('')
    // eslint-disable-next-line react-hooks/rules-of-hooks
    .map(() => useRef<ChildRefCalback>(null))
  const focusNextField: (key: number) => boolean = useCallback(
    (key) => {
      let length = 0
      formData.map((item) => {
        if (item?.horizontalComps) {
          length = length + item?.horizontalComps.length
        } else {
          length = length + 1
        }
      })
      if (inputRef[key]?.current) {
        inputRef[key]?.current?.focus()
        return true
      } else if (length - 1 !== key) {
        return focusNextField(key + 1)
      }
      return false
    },
    [formData, inputRef],
  )

  let inputRefKey = 0
  const getHorizontalComps = (horizontalComps: any, paddingH: number) => {
    if (horizontalComps.length === 0) {
      return null
    }
    return (
      <Box
        flexDirection={'row'}
        alignItems={'flex-start'}
        f={1}
        justify={horizontalFlex === 1 ? 'center' : 'flex-start'}
        flexWrap={'wrap'}>
        {horizontalComps.map(
          (
            {
              ComponentName,
              fieldName = '',
              hide = false,
              hideComponent,
              isLastEditableField = false,
              flex,
              ...rest
            }: any,
            index,
          ) => {
            const key = inputRefKey - 1
            inputRefKey = inputRefKey + 1
            if (hide) {
              return null
            }
            return (
              <Box
                flex={flex ?? horizontalFlex}
                ph={paddingH}
                paddingBottom={verticalSpacing}
                key={`${fieldName}_${index}`}>
                <ComponentName
                  fieldName={fieldName}
                  key={fieldName + index}
                  onChange={handleChange(fieldName)}
                  innerRef={inputRef[key]}
                  onSubmitEditing={() => {
                    if (inputRefKey - 1 !== key) {
                      focusNextField(key + 1)
                    } else if (isValid && onFormSubmit) {
                      onFormSubmit()
                    }
                  }}
                  onKeyPress={(event: any) => {
                    if (event.key === 'Tab' && inputRefKey - 1 !== key) {
                      event.preventDefault()
                      if (inputRefKey - 1 !== key) {
                        focusNextField(key + 1)
                      } else if (isValid && onFormSubmit) {
                        onFormSubmit()
                      }
                    }
                  }}
                  error={errors[fieldName]}
                  touched={touched[fieldName] || allTouched}
                  value={values[fieldName]}
                  setFieldValue={setFieldValue}
                  rightEnd={rightEnd}
                  onBlur={() => setFieldTouched(fieldName)}
                  isSubmitting={isSubmitting}
                  returnKeyType={isLastEditableField ? 'done' : 'next'}
                  changeStep={changeStep}
                  allValues={values}
                  formData={formData}
                  reloadStep={reloadStep}
                  {...rest}
                />
              </Box>
            )
          },
        )}
      </Box>
    )
  }

  return (
    <Box key={'formgenerator'} flex={verticalFlex} overflow={contentOverflow} padding={contentPadding}>
      {formData.map(
        (
          {
            ComponentName,
            fieldName,
            hide = false,
            isLastEditableField = false,
            horizontalComps = [],
            horizontalSpacing = 8,
            containerHeight,
            ...rest
          },
          index,
        ) => {
          let key = 0
          if (horizontalComps.length === 0) {
            key = inputRefKey
            inputRefKey = inputRefKey + 1
          }

          if (hide) {
            return null
          }
          return (
            <Box
              paddingVertical={verticalSpacing}
              zIndex={inputRefKey - key}
              key={fieldName + '-' + index}
              height={containerHeight}
              flexDirection={horizontalComps.length > 0 ? 'row' : undefined}>
              {horizontalComps.length === 0 && (
                <ComponentName
                  fieldName={fieldName}
                  key={'form-comp'}
                  onChange={handleChange(fieldName)}
                  innerRef={inputRef[key]}
                  onSubmitEditing={() => {
                    if (inputRefKey - 1 !== key) {
                      focusNextField(key + 1)
                    } else if (isValid && onFormSubmit) {
                      onFormSubmit()
                    }
                  }}
                  onKeyPress={(event: any) => {
                    if (event.key === 'Tab' && inputRefKey - 1 !== key) {
                      event.preventDefault()
                      if (inputRefKey - 1 !== key) {
                        focusNextField(key + 1)
                      } else if (isValid && onFormSubmit) {
                        onFormSubmit()
                      }
                    }
                  }}
                  error={errors[fieldName]}
                  touched={touched[fieldName] || allTouched}
                  value={values[fieldName]}
                  setFieldValue={setFieldValue}
                  rightEnd={rightEnd}
                  onBlur={() => setFieldTouched(fieldName)}
                  isSubmitting={isSubmitting}
                  returnKeyType={isLastEditableField ? 'done' : 'next'}
                  changeStep={changeStep}
                  allValues={values}
                  allErrors={errors}
                  formData={formData}
                  reloadStep={reloadStep}
                  {...rest}
                />
              )}
              {horizontalComps.length > 0 && getHorizontalComps(horizontalComps, horizontalSpacing)}
            </Box>
          )
        },
      )}
    </Box>
  )
}
